<template>
  <div class="paging" v-if="showPaging">
    <!-- ✅ PC / 平板：layout 和 pager-count 都随窗口宽度分档（见 screenTier） -->
    <el-pagination
      v-if="!isMobile"
      small
      background
      :current-page="current"
      :page-size="size"
      :page-sizes="effectivePageSizes"
      :layout="paginationLayout"
      :total="total"
      :pager-count="pagerCountAuto"
      @size-change="sizeChangeHandle"
      @current-change="currentChangeHandle"
    />

    <!-- ✅ 手机：外面套一层 .mobile-pagination（居中 + 紧凑字号），
         总数用左边的 .total-num 裸数字代替「共 x 条」文字，省地方 -->
    <div v-else class="mobile-pagination">
      <span class="total-num">{{ total }}</span>
      <el-pagination
        small
        background
        :current-page="current"
        :page-size="size"
        :layout="paginationLayout"
        :page-sizes="effectivePageSizes"
        :total="total"
        :pager-count="pagerCountAuto"
        @size-change="sizeChangeHandle"
        @current-change="currentChangeHandle"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'Paging',

  props: {
    showPaging: {
      type: Boolean,
      default: true
    },
    current: {
      type: Number,
      default: 1
    },
    size: {
      type: Number,
      default: 5
    },
    total: {
      type: Number,
      default: 0
    },
    pagerCount: {
      type: Number,
      default: 5
    },
    pageSizes: {
      type: Array,
    },

    /* ✅ 是否按窗口宽度切换「手机版分页」（默认 true = 维持原行为）。
       传 false：手机端也渲染 PC 那一套，用于「手机 / 电脑显示必须完全一致」的页面。 */
    responsive: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      /* ✅ 响应式用：当前窗口宽度。mounted 读一次，resize 时更新。
         之前是在 computed 里直接读 window.innerWidth —— 它不是响应式数据，
         只能靠 $forceUpdate() 硬刷，这里改成真正的响应式属性。 */
      windowWidth: (typeof window !== 'undefined' && window.innerWidth) || 1200
    };
  },

  computed: {
    /** ✅ 三档：mobile(≤768) / tablet(769–1200) / desktop(>1200)
        responsive=false → 永远按 desktop 处理（手机 / 电脑显示完全一致） */
    screenTier() {
      if (!this.responsive) return 'desktop';
      if (this.windowWidth <= 768) return 'mobile';   // 手机
      if (this.windowWidth <= 1200) return 'tablet';  // 小屏 / 平板
      return 'desktop';                               // 大屏 PC
    },

    /** 是否走手机版外层容器（居中 + 紧凑字号） */
    isMobile() {
      return this.screenTier === 'mobile';
    },

    /** ✅ 每页条数选项：父组件没传 pageSizes 时给一份默认，保证手机上也能选 */
    effectivePageSizes() {
      return (this.pageSizes && this.pageSizes.length)
        ? this.pageSizes
        : [10, 20, 50, 100];
    },

    /** ✅ layout 随宽度分档：越窄砍掉越占地方的项
          desktop：全显示（总数 + 每页条数 + 翻页 + 跳页）
          tablet ：砍掉跳页框 jumper（最占地方、手机上最好用度最低）
          mobile ：再砍掉 total 文字（改由外层 .total-num 裸数字显示） */
    paginationLayout() {
      switch (this.screenTier) {
        case 'mobile':
          return 'sizes, prev, pager, next';
        case 'tablet':
          return 'total, sizes, prev, pager, next';
        default:
          return 'total, sizes, prev, pager, next, jumper';
      }
    },

    /** ✅ 页码个数随宽度分档：越窄显示的页码越少
        ⚠️ 原来模板里用了 pagerCountAuto 但没定义，等于一直取 el-pagination 默认的 7 */
    pagerCountAuto() {
      switch (this.screenTier) {
        case 'mobile': return 3;
        case 'tablet': return 5;
        default: return 7;
      }
    }
  },

  mounted() {
    window.addEventListener('resize', this.onResize);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },

  methods: {
    /** resize 只更新 windowWidth 这一个响应式数据，其余全交给 computed 自己重算 */
    onResize() {
      this.windowWidth = window.innerWidth;
    },

    sizeChangeHandle(val) {
      this.$emit('sizeChange', val);
    },

    currentChangeHandle(val) {
      let target = Number(val);
      const max = Math.ceil(this.total / this.size);

      if (!target || target < 1) target = 1;
      if (target > max) target = max;

      this.$emit('currentChange', target);
    }
  }
};
</script>

<style scoped>
/* 外层容器 */
/* .paging {
  display: flex; 
  justify-content: flex-end;
  padding: 8px 0; 
} */

/* ✅ 电脑模式：分页里的输入框（每页条数下拉 / 前往 x 页）高度统一 22px
   ⚠️ 写在手机媒体查询之前：手机端后面同权重的规则会覆盖它 */
.paging ::v-deep .el-input__inner {
  height: 22px;
  line-height: 22px;
}
.paging ::v-deep .el-input__suffix,
.paging ::v-deep .el-input__suffix .el-input__icon {
  height: 22px;
  line-height: 22px;
}

/* ✅ 平板 / 小屏（769–1200）：配合 layout 砍掉 jumper，这里再把间距收紧一点，
      让「总数 + 每页条数 + 翻页」挤在一行里不至于太散 */
@media (min-width: 769px) and (max-width: 1200px) {
  .paging ::v-deep .el-pagination__jump {
    display: none;
  }
  .paging ::v-deep .el-pagination button,
  .paging ::v-deep .el-pager li {
    min-width: 24px;
    height: 24px;
    line-height: 24px;
    font-size: 12px;
  }
  .paging ::v-deep .el-pagination__sizes {
    margin-right: 4px;
  }
}

/* 手机 */
/* ✅ 手机端：整体字体变小 */
@media (max-width: 768px) {
/* ✅ 手机分页样式 */
.mobile-pagination {
  display: flex;
  align-items: center;
  justify-content: center;   /* ✅ 手机端整体居中显示 */
  gap: 4px;          /* 原 6px → 更紧凑 */
  font-size: 10px;   /* 原 12px */
  flex-wrap: wrap;   /* ✅ 加了每页条数下拉后，空间不够时换行，不横向溢出 */
}

.total-num {
  color: #606266;
  min-width: 22px;
  text-align: center;
  font-size: 10px;
}

  /* 覆盖 element 内部字体（原来是 5px，反而看不清） */
  ::v-deep .mobile-pagination .el-pagination {
    font-size: 10px;
    padding: 2px 0;
  }

  /* ✅ 所有分页项（上/下页按钮、页码、总页数等）统一紧凑 */
  ::v-deep .mobile-pagination .el-pagination button,
  ::v-deep .mobile-pagination .el-pager li {
    min-width: 20px !important;
    height: 20px !important;
    line-height: 20px !important;
    font-size: 10px !important;
    margin: 0 2px !important;
  }
  ::v-deep .mobile-pagination .el-pagination button {
    padding: 0 4px !important;
  }

  /* ✅ 前往 x 页：整体缩小 + margin-left 24px → 6px */
  ::v-deep .mobile-pagination .el-pagination__jump {
    margin-left: 6px !important;  /* 原 24px */
    font-size: 10px !important;
  }
  ::v-deep .mobile-pagination .el-pagination__editor.el-input {
    width: 34px !important;       /* 原 50px */
    margin: 0 3px !important;
  }
  ::v-deep .mobile-pagination .el-pagination__editor.el-input .el-input__inner {
    height: 20px !important;
    line-height: 20px !important;
    font-size: 10px !important;
    padding: 0 2px !important;
  }

  /* 输入框也同步小一点 */
  ::v-deep .mobile-pagination .el-input__inner {
    height: 20px;
    line-height: 20px;
    padding: 0 4px;
  }

  /* ✅ 每页条数下拉（sizes）：手机上要能点能看，不能被上面那条极端压缩影响 */
  ::v-deep .mobile-pagination .el-pagination__sizes {
    margin-right: 2px;
  }
  ::v-deep .mobile-pagination .el-pagination__sizes .el-input {
    width: 76px !important;
    margin: 0 !important;
  }
  ::v-deep .mobile-pagination .el-pagination__sizes .el-input__inner {
    height: 22px !important;
    line-height: 22px !important;
    font-size: 11px !important;
    padding: 0 18px 0 4px !important;
  }
  ::v-deep .mobile-pagination .el-pagination__sizes .el-input__suffix {
    height: 22px;
    line-height: 22px;
  }
  ::v-deep .mobile-pagination .el-pagination__sizes .el-input__icon {
    line-height: 22px;
  }
}


</style>